import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import Select from '../../select/select';
import {
  initialCustomer,
  MEDIA_CONSENT,
  ParticipantTypes,
  TERMS,
  TicketExchangeStores,
} from '../../../statics/vuon-concert-2025.statics';
import VuonConcert2025SectionFooter from './vuon-concert-2025-section-footer';
import { CustomerRegisterInfo } from '../types/vuon-concert-2025.types';
import Form from '../../../../../components/form/form';
import { vuonConcert2025FormSchema } from '../schemas/vuon-concert-2025-form.schema';
import DateInputGradient from '../../forms/date-input/date-input';
import InputGradient from '../../forms/input-gradient/input-gradient';
import {
  VuonConcertRegionSelectControl,
  VuonConcertRegion,
  UseRegionsHook,
} from '../../region-select/vuon-concert-region-select-control';
import {
  VuonConcertWardSelectControl,
  VuonConcertWard,
  UseWardsHook,
} from '../../ward-select/vuon-concert-ward-select-control';

export interface ConcertRegisterFormProps {
  handleSubmit: (formInput: CustomerRegisterInfo) => void;
  termsOfService?: string;
  mediaConsent?: string;
  placeHolderPhoneNumber?: string;
  placeHolderFullName?: string;
  placeHolderEmail?: string;
  placeHolderBirthDay?: string;
  placeHolderParticipantType?: string;
  placeHolderTicketExhcangeStores?: string;
  renderAdditionalEffect?: () => React.JSX.Element;
  isSubmitting?: boolean; // Thêm prop để track submit state
  // Thêm dependencies injection cho region và ward hooks
  useRegionsHook?: UseRegionsHook;
  useWardsHook?: UseWardsHook;
  // Thêm prop để disable toàn bộ form
  disabled?: boolean;
}

export const VuonConcert2025Form: React.FC<ConcertRegisterFormProps> = ({
    handleSubmit,
    termsOfService = TERMS,
    mediaConsent = MEDIA_CONSENT,
    placeHolderPhoneNumber = 'Nhập số điện thoại',
    placeHolderFullName = 'Nhập họ và tên',
    placeHolderEmail = 'Nhập Email',
    placeHolderBirthDay = 'Nhập ngày/tháng/năm sinh',
    placeHolderParticipantType = 'Chọn đối tượng tham gia',
    placeHolderTicketExhcangeStores = 'Chọn địa điểm',
    renderAdditionalEffect,
    isSubmitting = false,
    useRegionsHook,
    useWardsHook,
    disabled = true, // Mặc định disable toàn bộ form
}) => {
  // State management cho region và ward theo algorithm của RegionSelectionForm
  const [region, setRegion] = useState<VuonConcertRegion>({
    id: 0,
    value: '',
    label: '',
  });
  const [ward, setWard] = useState<VuonConcertWard>({
    value: 0,
    label: '',
  });

  const onRegionChange = (newRegion: VuonConcertRegion) => {
    setRegion(newRegion);
    // Reset ward khi region thay đổi - clear hoàn toàn data
    const resetWard = {
      value: 0,
      label: '',
    };
    setWard(resetWard);
  };

  const onWardChange = (newWard: VuonConcertWard) => {
    setWard(newWard);
  };

  const onSubmit = (formInput: CustomerRegisterInfo) => {
    // Không cho phép submit nếu form bị disable
    if (disabled) {
      return;
    }
    // Form validation sẽ tự động kiểm tra 2 checkbox đã được check chưa
    // Không cần kiểm tra manual nữa
    handleSubmit?.(formInput);
  };

  // Determine if region/ward support is available
  const hasRegionWardSupport = !!(useRegionsHook && useWardsHook);

  return (
    <Form<CustomerRegisterInfo>
      validationSchema={vuonConcert2025FormSchema(hasRegionWardSupport) as any}
      useFormProps={{
        defaultValues: {
          ...initialCustomer,
          termsOfService: false, // Thêm default values cho 2 checkbox
          mediaConsent: false,
        },
      }}
      onSubmit={onSubmit}
      className="w-full">
      {({ register, formState: { errors }, control, trigger, watch }) => {
        const handlePhoneNumberChange = async (phoneNumber: string) => {
          if (disabled) return; // Không xử lý nếu form bị disable
          const isValid = await trigger('telephone');
          if (!isValid || phoneNumber?.length !== 10) {
            return;
          }
        };

        return (
          <div className="w-full max-w-[1200px] flex flex-col justify-center gap-[56px] xl:gap-0 px-[15px] md:px-0">
            <div className="flex xl:flex-row flex-col justify-center xl:items-start items-center xl:mt-[70px] xl:mb-[90px]">
              <div>{renderAdditionalEffect?.()}</div>
              <div
                className="w-full max-w-[600px] flex flex-col h-full gap-2 xxs:mt-[50px] xs:mt-[50px] sm:mt-[50px] md:mt-[50px] xl:mt-0">
                <div>
                  <Controller
                    name="telephone"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputGradient
                        key="phone"
                        label="Số điện thoại *"
                        placeHolder={placeHolderPhoneNumber}
                        type="tel"
                        error={errors.telephone?.message}
                        value={value}
                        disabled={disabled}
                        readOnly={disabled}
                        onChange={e => {
                          if (disabled) return;
                          onChange(e);
                          const newValue = e.target.value;
                          handlePhoneNumberChange(newValue);
                        }}
                        classNameLabel="font-montserrat text-white text-[12px] md:text-[18px] font-bold"
                        className="font-montserrat"
                        aria-label="Số điện thoại"
                      />
                    )}
                  />
                </div>
                <div>
                  <InputGradient
                    key="name"
                    label="Họ và tên *"
                    placeHolder={placeHolderFullName}
                    type="text"
                    error={errors?.fullName?.message}
                    disabled={disabled}
                    readOnly={disabled}
                    classNameLabel="font-montserrat text-white text-[12px] md:text-[18px] font-bold"
                    className="font-montserrat"
                    {...register('fullName')}
                    aria-label="Họ và tên"
                  />
                </div>
                <div>
                  <InputGradient
                    key="email"
                    label="Email *"
                    placeHolder={placeHolderEmail}
                    type="text"
                    error={errors?.email?.message}
                    disabled={disabled}
                    readOnly={disabled}
                    classNameLabel="font-montserrat text-white text-[12px] md:text-[18px] font-bold"
                    className="font-montserrat"
                    {...register('email')}
                    aria-label="Email"
                  />
                </div>
                <div>
                  <Controller
                    name="birthday"
                    control={control}
                    render={({ field: { onChange } }) => (
                      <DateInputGradient
                        key="phone"
                        label="Ngày/Tháng/Năm sinh *"
                        placeHolder={placeHolderBirthDay}
                        type="tel"
                        error={errors.birthday?.message}
                        disabled={disabled}
                        readOnly={disabled}
                        classNameLabel="font-montserrat text-white text-[12px] md:text-[18px] font-bold"
                        className="font-montserrat"
                        onValueChange={newValue => {
                          if (disabled) return;
                          return onChange(newValue);
                        }}
                      />
                    )}
                  />
                </div>
                {/* Thêm Region và Ward fields theo algorithm của RegionSelectionForm */}
                {useRegionsHook && useWardsHook ? (
                  <div className="flex w-full flex-col justify-between gap-[16px] sm:flex-row sm:gap-[8px]">
                    <div className="flex w-full flex-col gap-[16px] md:w-[60%] md:flex-row md:gap-[8px]">
                      {/* Region area */}
                      <div className="flex-[1]">
                        <VuonConcertRegionSelectControl
                          name="region"
                          value={region}
                          onChange={disabled ? () => {} : onRegionChange}
                          control={control}
                          require
                          placeholder="Tỉnh/thành *"
                          label="Tỉnh/thành *"
                          disabled={disabled}
                          useRegionsHook={useRegionsHook}
                        />
                        {errors.region?.id?.message && (
                          <p className="mt-2 text-xs text-red-500 ltr:text-left rtl:text-right">
                            {errors.region?.id?.message}
                          </p>
                        )}
                      </div>

                      <div className="flex-[1]">
                        <VuonConcertWardSelectControl
                          name="ward"
                          regionId={region?.id || 0}
                          value={ward}
                          onChange={disabled ? () => {} : onWardChange}
                          control={control}
                          require
                          placeholder="Phường/xã *"
                          label="Phường/xã *"
                          disabled={disabled}
                          useWardsHook={useWardsHook}
                        />
                        {errors.ward?.value?.message && (
                          <p className="mt-2 text-xs text-red-500 ltr:text-left rtl:text-right">
                            {errors.ward?.value?.message}
                          </p>
                        )}
                      </div>
                    </div>
                    {/* !End Region area */}

                    <div className="w-full md:w-[40%]">
                      <InputGradient
                        required
                        label={`Địa chỉ *`}
                        {...register('street')}
                        type="text"
                        className="w-full"
                        error={errors.street?.message}
                        defaultValue={''}
                        disabled={disabled}
                        readOnly={disabled}
                        classNameLabel="font-montserrat text-white text-[12px] md:text-[18px] font-bold"
                        placeHolder="Nhập địa chỉ"
                        aria-label="Địa chỉ"
                      />
                    </div>
                  </div>
                ) : (
                  <div className="w-full">
                    <p className="text-yellow-300 text-sm font-montserrat">
                      * Để sử dụng tính năng chọn tỉnh/thành và phường/xã, vui lòng cung cấp useRegionsHook và
                      useWardsHook
                    </p>
                  </div>
                )}
                <div>
                  <Select
                    options={ParticipantTypes}
                    label="Đối tượng tham gia *"
                    placeHolder={placeHolderParticipantType}
                    key={null}
                    isDisable={disabled}
                    classNameLabel="font-montserrat text-white text-[12px] md:text-[18px] font-bold"
                    className="font-montserrat"
                    {...register('participantType')}
                  />
                  {errors?.participantType?.value?.message && (
                    <p className="mt-2 text-xs text-red-500">
                      {errors.participantType?.value?.message}
                    </p>
                  )}
                </div>
                <div>
                  <Select
                    options={TicketExchangeStores}
                    label="Chọn địa điểm đổi vé cứng *"
                    placeHolder={placeHolderTicketExhcangeStores}
                    key={null}
                    isDisable={disabled}
                    classNameLabel="font-montserrat text-white text-[12px] md:text-[18px] font-bold"
                    className="font-montserrat"
                    {...register('ticketExchangeStore')}
                  />
                  {errors?.ticketExchangeStore?.value?.message && (
                    <p className="mt-2 text-xs text-red-500">
                      {errors.ticketExchangeStore?.value?.message}
                    </p>
                  )}
                </div>
              </div>
            </div>
            <VuonConcert2025SectionFooter
              register={register}
              errors={errors}
              watch={watch}
              mediaConsent={mediaConsent}
              termsOfService={termsOfService}
              isSubmitting={isSubmitting || disabled}
            />
          </div>
        );
      }}
    </Form>
  );
};
